import { useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { atomWithDefault } from 'jotai/utils';
import { Tooltip, SkeletonLine } from '@crazyegginc/hatch';

import { useSelection, useQueryParams, usePermissions } from '/src/hooks';

import { DisplayCurrency } from '/src/features/_global/components/DisplayCurrency';
import { DetailCardHeaderElements } from './DetailCardHeaderElements';
import { DetailCardPopover } from './DetailCardPopover';
import { DetailCardTriggers } from './DetailCardTriggers';
import { DistributionChart } from './DistributionChart';
import { goalDetailSelectedTriggerType } from '/src/features/goals/store';
import { TRIGGER_CHART_TYPES, DISTRIBUTION_DATASET_TYPES } from '/src/features/goals/constants';
import { FEATURES } from '/src/features/_global/constants';

import { ReactComponent as InfoIcon } from '@crazyegginc/hatch/dist/images/icon-info-circle-outline.svg';
import { AppLink } from '/src/features/_global/components/AppLink';

const DEFAULT_DATASET_TYPE = DISTRIBUTION_DATASET_TYPES.CONVERSIONS;

const goalDistributionDatasetAtom = atomWithDefault(() => {
  const params = new URLSearchParams(window.location.search);
  const viewParam = params.get('view');
  return Object.values(DISTRIBUTION_DATASET_TYPES).includes(viewParam) ? viewParam : DEFAULT_DATASET_TYPE;
});

export function GoalDetailCard({ goal, counts, isFetchingCounts = true }) {
  const { id } = useParams();
  const { set: paramSet } = useQueryParams();
  const [type] = useAtom(goalDetailSelectedTriggerType);
  const [dataset, setDataset] = useAtom(goalDistributionDatasetAtom);
  const { currentSelection } = useSelection();
  const permissions = usePermissions();
  const canEditGoals = permissions.can('edit', FEATURES.GOALS).allowed;

  const totalConversionsCount = counts?.conversionsCount ?? 0;
  const uniqueConversionCount = counts?.uniqueConversionsCount ?? 0;
  const totalValue = counts?.totalValue;
  const triggersCounts = counts?.triggers ?? [];

  const hasValueSet = goal?.hasValue || totalValue?.displayWorth > 0;

  const isLoading = !goal;

  useEffect(() => {
    const param = new URLSearchParams(window.location.search);
    const viewParam = param.get('view');
    if (!viewParam || !Object.values(DISTRIBUTION_DATASET_TYPES).includes(viewParam)) {
      paramSet('view', DEFAULT_DATASET_TYPE);
    }
  }, [paramSet]);

  const setView = useCallback(
    (value) => {
      setDataset(value);
      paramSet('view', value);
    },
    [setDataset, paramSet],
  );

  useEffect(() => {
    if (!hasValueSet && dataset === DISTRIBUTION_DATASET_TYPES.VALUE) {
      setView(DISTRIBUTION_DATASET_TYPES.CONVERSIONS);
    }
  }, [hasValueSet, setView, dataset]);

  return (
    <div className="w-full">
      <div
        className="flex flex-col items-center rounded-[5px] border border-mystic-500 bg-white"
        style={{
          boxShadow: '0 4px 6px 0 rgba(7,31,49,0.07)',
        }}
      >
        <DetailCardHeaderElements>
          <DetailCardHeaderElements.Item
            active={dataset === DISTRIBUTION_DATASET_TYPES.CONVERSIONS}
            onCheck={() => {
              setView(DISTRIBUTION_DATASET_TYPES.CONVERSIONS);
            }}
          >
            <DetailCardHeaderElements.Title>
              <div className="flex items-center">
                <span className="mr-2.5">Total Conversions</span>
                <Tooltip
                  style={{ opacity: 1, padding: '0px', background: '#0a0b0e' }}
                  interactive={true}
                  placement="bottom"
                  tooltipContent={
                    <div className="w-[315px] text-left p-3.75 text-white text-xs leading-tight font-normal">
                      <p className="mb-3.75">
                        <span className="font-semibold">Total Conversions</span> refers to the number of total actions
                        taken on your website or app, regardless of how many visitors performed them.
                      </p>
                      <p className="mb-3.75">
                        <span className="font-semibold mb-1.25 block">Example</span>
                        <span>
                          If a single user clicks on a <span className="italic">Contact Us</span> button twice, that
                          counts as 2 total conversions.
                        </span>
                      </p>
                      <p className="mb-0">
                        <span className="font-semibold mb-1.25 block">
                          Why is the # of Total Conversions here different than # of Conversions on the A/B Testing
                          dashboard?
                        </span>
                        <span className="mb-1.25 block">
                          Within the A/B testing dashboard, conversions are only counted if a visitor lands on the
                          control or a variant before completing a Conversion.
                        </span>
                        <span>
                          In contrast, the Conversions dashboard includes all conversions, regardless of test
                          participation.
                        </span>
                      </p>
                    </div>
                  }
                >
                  <InfoIcon
                    className="h-3 w-3 -translate-y-px transform fill-current text-[#6bc3f7]"
                    aria-label="Info on conversion"
                  />
                </Tooltip>
              </div>
            </DetailCardHeaderElements.Title>
            {isFetchingCounts ? (
              <SkeletonLine className="!h-[37px] !rounded-sm" width="100%" />
            ) : (
              <DetailCardHeaderElements.Content data-testid="totalConversionsCountValue">
                {totalConversionsCount.toLocaleString()}
              </DetailCardHeaderElements.Content>
            )}
          </DetailCardHeaderElements.Item>

          <DetailCardHeaderElements.Item
            active={dataset === DISTRIBUTION_DATASET_TYPES.VISITORS}
            onCheck={() => {
              setView(DISTRIBUTION_DATASET_TYPES.VISITORS);
            }}
          >
            <DetailCardHeaderElements.Title>
              <div className="flex items-center">
                <span className="mr-2.5">Visitors Converting</span>
                <Tooltip
                  style={{ opacity: 1, padding: '0px', background: '#0a0b0e' }}
                  interactive={true}
                  placement="bottom"
                  tooltipContent={
                    <div className="w-[315px] text-left p-3.75 text-white text-xs leading-tight font-normal">
                      <p className="mb-3.75">
                        <span className="font-semibold mb-1.25 block">What is Visitors Converting?</span>
                        <span>
                          This is the number of unique visitors who completed at least one conversion during the
                          selected date range. Each visitor is only counted on the first day they convert within the
                          selected date range, regardless of how many times they convert.
                        </span>
                      </p>
                      <p className="mb-1.25 font-semibold">Example #1</p>
                      <ul className="mb-3.75 space-y-1.25 list-disc pl-7">
                        <li>
                          <span className="font-semibold">Monday:</span> Visitor A converts 2 times, Visitor B converts
                          1 time → 2 unique visitors
                        </li>
                        <li>
                          <span className="font-semibold">Tuesday:</span> Visitor A converts 3 times, Visitor B converts
                          4 times → 0 visitors
                        </li>
                      </ul>
                      <p className="mb-1">
                        If the calendar filter is set to Monday &ndash; Tuesday, the Visitors Converting count will be 2
                        &ndash; both Visitor A and B will only count on Monday. Tuesday will show 0 unique visitors.
                      </p>
                      <p className="mb-1">
                        ⚠️ Numbers can change depending on the date range selected. In the above example if you change
                        the date range to just Tuesday by itself it will then show 2 visitors for Tuesday.
                      </p>
                      <p className="mb-0">Use Total Conversions to see all conversions.</p>
                    </div>
                  }
                >
                  <InfoIcon
                    className="h-3 w-3 -translate-y-px transform fill-current text-[#6bc3f7]"
                    aria-label="Info on conversion"
                  />
                </Tooltip>
              </div>
            </DetailCardHeaderElements.Title>
            {isFetchingCounts ? (
              <SkeletonLine className="!h-[37px] !rounded-sm" width="100%" />
            ) : (
              <DetailCardHeaderElements.Content data-testid="uniqueConversionCountValue">
                {uniqueConversionCount.toLocaleString()}
              </DetailCardHeaderElements.Content>
            )}
          </DetailCardHeaderElements.Item>

          <div className="relative flex">
            <DetailCardHeaderElements.Item
              active={dataset === DISTRIBUTION_DATASET_TYPES.VALUE}
              isButton={hasValueSet}
              blockType={!hasValueSet}
              onCheck={() => {
                if (hasValueSet) {
                  setView(DISTRIBUTION_DATASET_TYPES.VALUE);
                }
              }}
            >
              <DetailCardHeaderElements.Title>
                Conversion Value {hasValueSet && totalValue && `(${totalValue.displayCurrency})`}
              </DetailCardHeaderElements.Title>
              <DetailCardHeaderElements.Content data-testid="goalValueValue">
                {isFetchingCounts ? (
                  <SkeletonLine className="!h-[37px] !rounded-sm" width="100%" />
                ) : hasValueSet ? (
                  <DisplayCurrency
                    value={{
                      displayWorth: totalValue.displayWorth,
                      displayCurrency: totalValue.displayCurrency,
                      isConverted: false,
                    }}
                    precision={10000}
                    textOnly={true}
                    appendCurrency={false}
                    isTruncate={true}
                  />
                ) : canEditGoals ? (
                  <AppLink to={`/goals/${id}/edit-value`} className="text-link">
                    Set Conversion value
                  </AppLink>
                ) : (
                  <span className="text-body-3">Not set</span>
                )}
              </DetailCardHeaderElements.Content>
            </DetailCardHeaderElements.Item>
            {hasValueSet && canEditGoals ? (
              <div className="absolute right-3 top-4 text-lynch-500">
                <DetailCardPopover />
              </div>
            ) : null}
          </div>
        </DetailCardHeaderElements>
        <div className="py-10 border-b-2 border-mystic-500 w-full px-5">
          <div className="relative flex h-[300px] min-w-[500px] flex-1">
            {!isFetchingCounts && (
              <DistributionChart
                goal={goal}
                triggerSelection={currentSelection}
                dataset={dataset}
                isSeparate={type === TRIGGER_CHART_TYPES.SEPARATE}
              />
            )}
          </div>
        </div>
        {!isLoading && (
          <DetailCardTriggers
            goal={goal}
            triggersCounts={triggersCounts}
            hasValueSet={hasValueSet}
            isFetchingCounts={isFetchingCounts}
          />
        )}
      </div>
    </div>
  );
}
